/* Blog listing page showing all published blogs
   Converted from Next.js to Vite/React
   Features: Featured blog shown at top (due to database sorting), remaining blogs in hero format and card grid
   Updated: Supports blog images from storage bucket, card layout for older posts, search and pagination
   Updated: Featured blog appears at top without special styling, maintaining consistent design
   Updated: Fixed navigation by using Link directly as button instead of wrapping button elements
   Updated: Mobile/tablet (sm/md) shows all blogs in single column with homepage overlay style (title only), paginated at 10 per page
   Updated: Desktop (lg+) maintains hero blogs + card grid layout with separate pagination */

import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, Search } from 'lucide-react';
import ContactBar from '@/components/ContactBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import { supabase } from '@/lib/supabase';
import { getBlogImageUrl } from '@/lib/image-url';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface Blog {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  image_url: string;
  image_storage_path?: string;
  published_at: string;
  content: string;
  views: number;
  is_featured: boolean;
}

export default function BlogsPage() {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [filteredBlogs, setFilteredBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 9;

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredBlogs(blogs);
    } else {
      const filtered = blogs.filter(blog =>
        blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        blog.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredBlogs(filtered);
    }
    setCurrentPage(1);
  }, [searchQuery, blogs]);

  const fetchBlogs = async () => {
    try {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('blogs')
        .select('*')
        .eq('published', true)
        .or(`scheduled_at.is.null,scheduled_at.lte.${now}`)
        .order('is_featured', { ascending: false })
        .order('published_at', { ascending: false });

      if (error) throw error;
      setBlogs(data || []);
      setFilteredBlogs(data || []);
    } catch (error) {
      // Error fetching blogs
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const estimateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const words = content.trim().split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  };

  const heroBlogs = filteredBlogs.slice(0, 2);
  const remainingBlogs = filteredBlogs.slice(2);

  // For desktop: paginate remaining blogs after hero section
  const totalPages = Math.ceil(remainingBlogs.length / blogsPerPage);
  const startIndex = (currentPage - 1) * blogsPerPage;
  const endIndex = startIndex + blogsPerPage;
  const paginatedBlogs = remainingBlogs.slice(startIndex, endIndex);

  // For mobile: paginate all blogs in single column
  const mobileBlogsPerPage = 10;
  const totalMobilePages = Math.ceil(filteredBlogs.length / mobileBlogsPerPage);
  const mobileStartIndex = (currentPage - 1) * mobileBlogsPerPage;
  const mobileEndIndex = mobileStartIndex + mobileBlogsPerPage;
  const paginatedMobileBlogs = filteredBlogs.slice(mobileStartIndex, mobileEndIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Tips & advies voor tuinonderhoud | Buitn Planten</title>
          <meta name="description" content="Ontdek nuttige tips, advies en inspiratie voor uw tuin. Lees onze blogs over tuinplanten, haagplanten, onderhoud en meer." />
          <meta name="robots" content="index, follow" />
          <meta property="og:title" content="Tips & advies voor tuinonderhoud | Buitn Planten" />
          <meta property="og:description" content="Ontdek nuttige tips, advies en inspiratie voor uw tuin." />
          <meta property="og:url" content="https://buitnplanten.nl/blogs" />
          <meta property="og:image" content="https://buitnplanten.nl/assets/sections/blogs.png" />
          <meta property="og:type" content="website" />
          <link rel="canonical" href="https://buitnplanten.nl/blogs" />
        </Helmet>
        <div className="min-h-screen bg-white">
          <ContactBar />
          <Header />
          <div className="flex items-center justify-center pt-0 pb-">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-jv-primary"></div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Tips & advies voor tuinonderhoud | Buitn Planten</title>
        <meta name="description" content="Ontdek nuttige tips, advies en inspiratie voor uw tuin. Lees onze blogs over tuinplanten, haagplanten, onderhoud en meer." />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Tips & advies voor tuinonderhoud | Buitn Planten" />
        <meta property="og:description" content="Ontdek nuttige tips, advies en inspiratie voor uw tuin." />
        <meta property="og:url" content="https://buitnplanten.nl/blogs" />
        <meta property="og:image" content="https://buitnplanten.nl/assets/sections/blogs.png" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://buitnplanten.nl/blogs" />
      </Helmet>

      <div className="min-h-screen bg-white">
        <ContactBar />
        <Header />

        <main className="pt-8 pb-16 bg-white">
          <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
            <PageBreadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Inspiratie' }]} />
            <div className="text-center mb-16">
              <h1 className="text-5xl md:text-6xl font-bold text-jv-primary mb-6">
                Blogs
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Ontdek handige tips en inspiratie voor het onderhouden van haag en tuin
              </p>

              {/* Search Bar */}
              <div className="max-w-2xl mx-auto">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Zoek in blogs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 pr-4 py-6 text-lg rounded-full border-2 border-jv-primary focus:border-jv-primary transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Mobile/Tablet View (sm/md) - Single column grid with all blogs */}
            <div className="lg:hidden space-y-12 mb-12">
              {paginatedMobileBlogs.map((blog: Blog, index: number) => (
                <Link key={blog.id} to={`/blogs/${blog.slug}`} className="block">
                  <div
                    className="relative overflow-hidden w-full min-h-[250px] cursor-pointer group"
                    style={{
                      borderTopLeftRadius: index % 2 === 0 ? '60px' : '0px',
                      borderBottomRightRadius: index % 2 === 0 ? '60px' : '0px',
                      borderTopRightRadius: index % 2 === 1 ? '60px' : '0px',
                      borderBottomLeftRadius: index % 2 === 1 ? '60px' : '0px'
                    }}
                  >
                    {/* Background Image */}
                    <img
                      src={
                        blog.image_storage_path
                          ? getBlogImageUrl(blog.image_storage_path, 'medium')
                          : blog.image_url || '/assets/sections/blogs.png'
                      }
                      alt={blog.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />

                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                    {/* Title at bottom */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 px-8 z-10">
                      <h2 className="text-sm md:text-md font-bold text-white leading-tight">
                        {blog.title}
                      </h2>
                    </div>
                  </div>
                </Link>
              ))}

              {/* Mobile Pagination */}
              {totalMobilePages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-12">
                  <Button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    variant="outline"
                    className="px-3 py-2 text-sm border-2 border-jv-primary text-jv-primary hover:bg-jv-primary hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Vorige
                  </Button>

                  <div className="flex gap-1">
                    {Array.from({ length: totalMobilePages }, (_, i) => i + 1).map((page) => (
                      <Button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        variant={currentPage === page ? "default" : "outline"}
                        className={`px-3 py-2 text-sm ${
                          currentPage === page
                            ? 'bg-jv-primary text-white'
                            : 'border-2 border-jv-primary text-jv-primary hover:bg-jv-primary hover:text-white'
                        }`}
                      >
                        {page}
                      </Button>
                    ))}
                  </div>

                  <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalMobilePages}
                    variant="outline"
                    className="px-3 py-2 text-sm border-2 border-jv-primary text-jv-primary hover:bg-jv-primary hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Volgende
                  </Button>
                </div>
              )}
            </div>

            {/* Desktop View (lg+) - Hero Blogs + Card Grid */}
            <div className="hidden lg:block">
              {/* Hero Blogs - First 2 blogs (featured blog will be first due to sorting) */}
              <div className="space-y-24 mb-24">
                {heroBlogs.map((blog: Blog, index: number) => {
                  const isOdd = index % 2 === 1;

                  return (
                    <div
                      key={blog.id}
                      className={`${isOdd ? 'py-12 -mx-8 px-8 sm:-mx-12 sm:px-12 -mx-16 px-16' : ''}`}
                    >
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
                        <div className={`${isOdd ? 'lg:order-2' : 'lg:order-1'}`}>
                          <div
                            className="relative h-[400px] overflow-hidden bg-jv-tertiary shadow-lg hover:shadow-xl transition-shadow duration-300"
                            style={{
                              borderTopLeftRadius: '120px',
                              borderBottomRightRadius: '120px'
                            }}
                          >
                            <img
                              src={
                                blog.image_storage_path
                                  ? getBlogImageUrl(blog.image_storage_path, 'large')
                                  : blog.image_url || '/assets/sections/blogs.png'
                              }
                              alt={blog.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>

                        <div className={`${isOdd ? 'lg:order-1' : 'lg:order-2'}`}>
                          <div className={`flex items-center gap-4 text-sm mb-4 text-gray-600`}>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(blog.published_at)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              <span>{estimateReadTime(blog.content)} min lezen</span>
                            </div>
                          </div>

                          <h2 className={`text-3xl md:text-4xl font-bold mb-4 leading-tight ${isOdd ? 'text-jv-accent' : 'text-jv-primary'}`}>
                            {blog.title}
                          </h2>

                          <p className={`text-lg mb-6 leading-relaxed text-gray-600`}>
                            {blog.excerpt}
                          </p>

                          <Link
                            to={`/blogs/${blog.slug}`}
                            className={`group inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-colors duration-300 ${
                              isOdd
                                ? 'bg-jv-accent text-white hover:bg-jv-primary'
                                : 'bg-jv-primary text-white hover:bg-jv-accent'
                            }`}
                          >
                            <span>Lees meer</span>
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Blog Cards - Remaining blogs */}
              {remainingBlogs.length > 0 && (
                <>
                  <div className="mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-jv-primary text-center">
                      Meer blogs
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                    {paginatedBlogs.map((blog: Blog) => (
                      <Link
                        key={blog.id}
                        to={`/blogs/${blog.slug}`}
                        className="group"
                      >
                        <div className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                          <div className="relative h-[250px] overflow-hidden bg-jv-tertiary">
                            <img
                              src={
                                blog.image_storage_path
                                  ? getBlogImageUrl(blog.image_storage_path, 'large')
                                  : blog.image_url || '/assets/sections/blogs.png'
                              }
                              alt={blog.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>

                          <div className="p-6 flex flex-col flex-grow">
                            <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                <span>{formatDate(blog.published_at)}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                <span>{estimateReadTime(blog.content)} min</span>
                              </div>
                            </div>

                            <h3 className="text-xl font-bold text-jv-primary mb-3 line-clamp-2 group-hover:text-jv-accent transition-colors duration-300">
                              {blog.title}
                            </h3>

                            <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-grow">
                              {blog.excerpt}
                            </p>

                            <div className="flex items-center gap-2 text-jv-primary font-semibold text-sm group-hover:text-jv-accent transition-colors duration-300">
                              <span>Lees meer</span>
                              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {/* Desktop Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-12">
                      <Button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        variant="outline"
                        className="px-4 py-2 border-2 border-jv-primary text-jv-primary hover:bg-jv-primary hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Vorige
                      </Button>

                      <div className="flex gap-2">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <Button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            variant={currentPage === page ? "default" : "outline"}
                            className={`px-4 py-2 ${
                              currentPage === page
                                ? 'bg-jv-primary text-white'
                                : 'border-2 border-jv-primary text-jv-primary hover:bg-jv-primary hover:text-white'
                            }`}
                          >
                            {page}
                          </Button>
                        ))}
                      </div>

                      <Button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        variant="outline"
                        className="px-4 py-2 border-2 border-jv-primary text-jv-primary hover:bg-jv-primary hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Volgende
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>

            {(!filteredBlogs || filteredBlogs.length === 0) && !loading && (
              <div className="text-center py-16">
                <p className="text-xl text-gray-600">
                  {searchQuery ? 'Geen blogs gevonden voor je zoekopdracht.' : 'Nog geen blogs beschikbaar. Kom binnenkort terug voor handige tuintips!'}
                </p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
